import {
  useClientDetail,
  useEmailAnalysisQuestionnaireEmptyPdf,
  useEmailAnalysisQuestionnairePdf,
  useGetAnalysisClients,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AnalysisClientItemRelationType,
  type EmailQuestionnairePdfBadRequestError,
  EmailQuestionnairePdfBadRequestErrorCode,
  type EmailQuestionnairePdfNotFoundError,
  RelatedClientDetailType,
} from "Infrastructure/ApiZfp/Schema";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { CLIENT_DETAIL_RETRY, selectClientDetailClient } from "Infrastructure/Utils/ClientUtils";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { useContext } from "react";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";

export type QuestionnairePdfType = "empty" | "filled";

export const useQuestionnairePdf = () => {
  const { t } = useResource();
  const { analysisId, versionId } = useContext(AnalysisDetailContext);

  const { data: clientRelationsData } = useGetAnalysisClients(analysisId, versionId);
  const client = clientRelationsData?.clients.find((client) => client.relationType === AnalysisClientItemRelationType.Client);

  const { data: clientData } = useClientDetail(client?.guid ?? "", {
    query: { enabled: !!client?.guid, select: selectClientDetailClient, retry: CLIENT_DETAIL_RETRY },
  });
  const clientEmail = clientData && clientData.type !== RelatedClientDetailType.related ? clientData.email : null;

  const emailMutationOptions = {
    mutation: {
      onSuccess: () => toast.success(t(Resources.Questionnaire.Evaluation.SendPdf.Sent)),
      onError: (error: EmailQuestionnairePdfBadRequestError | EmailQuestionnairePdfNotFoundError) => {
        if (error?.code === EmailQuestionnairePdfBadRequestErrorCode.client_email_missing) {
          toast.error(t(Resources.Questionnaire.Evaluation.SendPdf.EmailInvalid));
        } else if (error?.code === EmailQuestionnairePdfBadRequestErrorCode.client_birth_date_missing) {
          toast.error(t(Resources.Questionnaire.Evaluation.SendPdf.BirthDateMissing));
        } else {
          toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
        }
      },
    },
  };

  const { mutate: sendEmptyPdf, isPending: isSendingEmptyPdf } = useEmailAnalysisQuestionnaireEmptyPdf(emailMutationOptions);
  const { mutate: sendFilledPdf, isPending: isSendingFilledPdf } = useEmailAnalysisQuestionnairePdf(emailMutationOptions);

  const isSending: Record<QuestionnairePdfType, boolean> = { empty: isSendingEmptyPdf, filled: isSendingFilledPdf };

  const sendPdf = (type: QuestionnairePdfType) => {
    const send = type === "empty" ? sendEmptyPdf : sendFilledPdf;
    send({ analysisId, versionId });
  };

  const openPreview = (type: QuestionnairePdfType) => {
    window.open(`${window.location.origin}${AppRouting.QuestionnaireDocument.path(analysisId, versionId, type)}`, "_blank", "noopener");
  };

  return { clientEmail, isSending, sendPdf, openPreview };
};
